import { Observable } from "rxjs/internal/Observable";
import { Subscription } from "rxjs";
import { LayoutService } from "../layout.service";
import { PageService } from "../../core/services/page.service";
import { AuthService } from "../../core/services/auth.service";
import { Router } from "@angular/router";
import { Component, HostListener, OnDestroy, OnInit } from "@angular/core";

@Component({
    selector: 'app-sidebar',
    templateUrl: './sidebar.component.html',
    styleUrls: ['./sidebar.component.css'],
    standalone: false
})
export class SidebarComponent implements OnInit, OnDestroy {
  isCollapsed$: Observable<boolean>;
  isMobileMenuOpen$: Observable<boolean>;

  // Plain copies of the shared layout state, for use in methods.
  private isCollapsed = false;
  private isMobileMenuOpen = false;
  private layoutSubs = new Subscription();

  notificationCount = 0;
  showNotifications = false;
  showProfileMenu = false;

  // Tooltip shown next to icons while the desktop sidebar is icon-only
  tooltipVisible = false;
  tooltipText = '';
  tooltipTop = 0;
  tooltipLeft = 0;

  constructor(
    private layoutService: LayoutService,
    private pageService: PageService,
    private authService: AuthService, // Ensure this is implemented
    private router: Router
  ) {
    this.isCollapsed$ = this.layoutService.sidebarCollapsed$;
    this.isMobileMenuOpen$ = this.layoutService.mobileMenuOpen$;
  }

  ngOnInit(){
    //this.getNotification();
    this.authService.unreadCount$.subscribe(() => {
      this.getNotification(); // Refresh unread count when triggered
    });

    this.layoutSubs.add(this.isCollapsed$.subscribe(v => this.isCollapsed = v));
    this.layoutSubs.add(this.isMobileMenuOpen$.subscribe(v => {
      this.isMobileMenuOpen = v;
      if (v) { this.hideTooltip(); }
    }));
  }

  ngOnDestroy() {
    this.layoutSubs.unsubscribe();
  }

  toggleSidebar() {
    this.hideTooltip();
    this.layoutService.toggleSidebar();
  }

  closeMobileMenu() {
    this.layoutService.closeMobileMenu();
  }

  // The drawer only exists below 1024px. If the window grows past that while
  // it is open, close it so it doesn't re-appear when the window shrinks again.
  @HostListener('window:resize')
  onResize() {
    if (this.isMobileMenuOpen && window.innerWidth >= 1024) {
      this.layoutService.closeMobileMenu();
    }
  }

  @HostListener('document:keydown.escape')
  onEscape() {
    if (this.isMobileMenuOpen) {
      this.layoutService.closeMobileMenu();
    }
  }

  onOptionClick(page: string) {
    this.pageService.setCurrentPage(page);
    localStorage.setItem('navItem', page)
    // this.router.navigate([`/${page.toLowerCase()}`]);
    this.closeMobileMenu(); // no-op on desktop
  }
  
  isSettingsActive(): boolean {
    const currentUrl = this.router.url;
    return currentUrl.startsWith('/settings') || currentUrl.startsWith('/profile') || currentUrl.startsWith('/subscription')|| currentUrl.startsWith('/country');
  }

  isHealthActive(): boolean {
    const currentUrl = this.router.url;
    return currentUrl.startsWith('/health/dashbord') || currentUrl.startsWith('/health/medication') || currentUrl.startsWith('/health/profile/immunization') || 
    currentUrl.startsWith('/health/profile/insurance') || currentUrl.startsWith('/health/care-plan') || currentUrl.startsWith('/health/health-log') || currentUrl.startsWith('/health/devices') ||
    currentUrl.startsWith('/health/profile/familyhistory') || currentUrl.startsWith('/health/profile/medicalcondition') || currentUrl.startsWith('/health/profile/allergy') ||
    currentUrl.startsWith('/health/device');
  }

  onLogout() {
    // Preserve "Remember Me" data before clearing localStorage
    const rememberedEmail = localStorage.getItem('remembered_email');
    const rememberedPassword = localStorage.getItem('remembered_password');
    const rememberedChecked = localStorage.getItem('remembered_checked');

    // Clear localStorage but keep "Remember Me" credentials
    localStorage.clear();
    //sessionStorage.clear();

    if (rememberedChecked === 'true') {
        localStorage.setItem('remembered_email', rememberedEmail || '');
        localStorage.setItem('remembered_password', rememberedPassword || '');
        localStorage.setItem('remembered_checked', rememberedChecked);
    }
    
    this.closeMobileMenu();
    this.router.navigate(['/login']);
  }

  toggleNotifications() {
    this.showNotifications = !this.showNotifications;
    this.showProfileMenu = false;
  }

  toggleProfileMenu() {
    this.showProfileMenu = !this.showProfileMenu;
    this.showNotifications = false;
  }

  getNotification() {
    this.authService.get_notifications().subscribe((res: any) => {
      if (!res.error && res.data?.notifications?.length) {
        const allNotifications = res.data.notifications;
        // Count how many notifications have status 'unread'
        this.notificationCount = allNotifications.filter(
          (notif: any) => notif.status === 'unread'
        ).length;
      } else {
        this.notificationCount = 0;
      }
    });
  }
  
  gotoDashboard() {
    this.router.navigate(['/dashboard']);
    this.closeMobileMenu();
  }

  // ---- Tooltip (desktop icon-only mode only) ----
  showTooltip(event: MouseEvent | FocusEvent, text: string) {
    if (!this.isCollapsed || this.isMobileMenuOpen) {
      return;
    }
    const target = event.currentTarget as HTMLElement;
    if (!target) {
      return;
    }
    const rect = target.getBoundingClientRect();
    this.tooltipText = text;
    this.tooltipTop = rect.top + rect.height / 2;
    this.tooltipLeft = rect.right + 10;
    this.tooltipVisible = true;
  }

  hideTooltip() {
    this.tooltipVisible = false;
  }
}